import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

import {
  fetchAthleteGoals,
  fetchAthleteProfile,
  fetchAvailability,
  saveAuthoritativeTrainingGoal,
} from "@/api/athleteGoalsApi";
import { fetchTrainingIntake, previewPlanStart } from "@/api/trainingIntakeApi";
import { fetchCurrentCoachingDecision } from "@/api/coachingDecisionApi";
import {
  fetchCurrentActiveTrainingPlan,
  generateTrainingPlan,
} from "@/api/trainingPlanApi";
import { CoachingSectionCard } from "@/components/coaching/CoachingSectionCard";
import { TrainingPlanDraftReview } from "@/components/coaching/TrainingPlanDraftPanel";
import { Button } from "@/components/ui/button";
import { describeMissingInformation } from "@/lib/missingInformationCopy";
import {
  PLAN_GENERATION_RESULT_KIND,
  interpretPlanGenerationResult,
} from "@/lib/interpretPlanGenerationResult";
import {
  coachingDecisionBlocksPlanGeneration,
  coachingDecisionPrerequisiteMessage,
} from "@/lib/planGenerationPrerequisites";
import {
  loadQuickPlanPrefill,
  quickPlanLifecycleErrorMessage,
  resolveQuickPlanHorizonWeeks,
} from "@/lib/quickPlanUpdate";
import { ACTIVE_PLAN_CHANGED_EVENT } from "@/lib/activePlanSync";
import {
  FUTURE_PLAN_NOT_SUPPORTED,
  futurePlanBlockerMessage,
  startValidatedTrainingPlan,
} from "@/lib/trainingPlanActivation";
import {
  WEEKDAY_OPTIONS,
  buildAthleteGoalPayload,
  buildProposedTrainingContext,
  dayMaxMinutesFromAvailability,
  categoriesForSport,
  categoriesForSportWithCustom,
  isEventCategory,
  sportConfig,
  targetDateFromHorizonMonths,
  GOAL_HORIZON_MONTH_OPTIONS,
  TRAINING_GOAL_SPORTS,
  START_PREFERENCE_OPTIONS,
  QUICK_PLAN_DURATION_OPTIONS,
  ISO_WEEKDAY_NAMES,
  CUSTOM_GOAL_CATEGORY,
  CUSTOM_GOAL_STORAGE_CATEGORY,
  resolveDaySessionCap,
} from "@/lib/trainingGoalOptions";

const inputCls = "app-control text-sm w-full";

const WEEKDAY_NAMES = {
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday",
  7: "Sunday",
};

const SLIDES = [
  { id: "sport-goal", title: "Sport & goal", short: "Goal" },
  { id: "target", title: "Target", short: "Target" },
  { id: "days", title: "Training days", short: "Days" },
  { id: "availability", title: "Training availability", short: "Time" },
  { id: "schedule", title: "Plan schedule", short: "Schedule" },
  { id: "review", title: "Review & start", short: "Review" },
];

const selectedWeekdaysFromAvailability = (availability) => {
  const days = availability?.weekly_pattern?.days || [];
  return days
    .map((day) => Number(day.weekday))
    .filter((day) => day >= 1 && day <= 7);
};

const newRequestId = (prefix) => {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return `${prefix}-${globalThis.crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const creatorValidationError = (state, throughSlide = 5) => {
  if (throughSlide >= 1) {
    if (!String(state.originalText || "").trim()) {
      return { slide: 1, message: "Describe your goal in a sentence." };
    }
    if (state.goalCategory === CUSTOM_GOAL_CATEGORY && !String(state.customGoalName || "").trim()) {
      return { slide: 1, message: "Name your custom goal." };
    }
  }
  if (throughSlide >= 3 && (state.weekdays || []).length === 0) {
    return { slide: 3, message: "Choose at least one training day." };
  }
  if (throughSlide >= 4) {
    const missing = (state.weekdays || []).filter((day) => {
      const minutes = Number(state.dayMaxMinutes?.[day]);
      return !Number.isInteger(minutes) || minutes < 5 || minutes > 600;
    });
    if (missing.length > 0) {
      return { slide: 4, message: "Enter training minutes (5–600) for each selected day." };
    }
  }
  if (throughSlide >= 5) {
    if (!state.startPreference) {
      return { slide: 5, message: "Choose when your plan should start." };
    }
    if (state.startPreference === "CUSTOM_DATE" && !state.customStartDate) {
      return { slide: 5, message: "Choose a custom start date." };
    }
    if (!Number.isInteger(Number(state.planHorizonWeeks)) || Number(state.planHorizonWeeks) < 1) {
      return { slide: 5, message: "Choose how many weeks this training block should cover." };
    }
    if (state.hasLivePlan && !state.activePlanConflictChoice) {
      return {
        slide: 5,
        message: "Choose whether to replace your current plan or finish it first.",
      };
    }
  }
  return null;
};

const previewFingerprint = (state) => JSON.stringify({
  sportId: state.sportId,
  goalCategory: state.goalCategory,
  customGoalName: state.customGoalName,
  originalText: state.originalText,
  targetDate: state.targetDate,
  horizonMode: state.horizonMode,
  horizonMonths: state.horizonMonths,
  noEventDate: state.noEventDate,
  distanceKm: state.distanceKm,
  ftpWatts: state.ftpWatts,
  weekdays: state.weekdays,
  dayMaxMinutes: state.dayMaxMinutes,
  preferredLongSessionDay: state.preferredLongSessionDay,
  longestSessionMinutes: state.longestSessionMinutes,
  startPreference: state.startPreference,
  customStartDate: state.customStartDate,
  planHorizonWeeks: state.planHorizonWeeks,
  activePlanConflictChoice: state.activePlanConflictChoice,
});

export const TrainingGoalSetup = ({
  onSaved,
  onPlanGenerated,
  incomingDraft = null,
  onIncomingDraftConsumed = null,
  focusToken = 0,
  loadGoals = fetchAthleteGoals,
  loadProfile = fetchAthleteProfile,
  loadAvailability = fetchAvailability,
  loadIntake = fetchTrainingIntake,
  loadCoachingDecision = fetchCurrentCoachingDecision,
  loadActivePlan = fetchCurrentActiveTrainingPlan,
  loadCurrentLifecycle = null,
  saveGoal = saveAuthoritativeTrainingGoal,
  generatePlan = generateTrainingPlan,
  previewStart = previewPlanStart,
  saveGenerationParams = async () => ({}),
  startPlan = startValidatedTrainingPlan,
  restoreContext = async () => ({}),
}) => {
  const [sportId, setSportId] = useState("cycling");
  const [goalCategory, setGoalCategory] = useState("ride_longer_without_fatigue");
  const [customGoalName, setCustomGoalName] = useState("");
  const [originalText, setOriginalText] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [horizonMode, setHorizonMode] = useState("unset");
  const [horizonMonths, setHorizonMonths] = useState("8");
  const [noEventDate, setNoEventDate] = useState(false);
  const [distanceKm, setDistanceKm] = useState("");
  const [ftpWatts, setFtpWatts] = useState("");
  const [weekdays, setWeekdays] = useState([]);
  const [startPreference, setStartPreference] = useState("START_TOMORROW");
  const [customStartDate, setCustomStartDate] = useState("");
  const [planHorizonWeeks, setPlanHorizonWeeks] = useState(4);
  const [preferredLongSessionDay, setPreferredLongSessionDay] = useState("");
  const [activePlanConflictChoice, setActivePlanConflictChoice] = useState("");
  const [hasLivePlan, setHasLivePlan] = useState(false);
  const [longestSessionMinutes, setLongestSessionMinutes] = useState("90");
  const [currentGoal, setCurrentGoal] = useState(null);
  const [existingAvailability, setExistingAvailability] = useState(null);
  const [dayMaxMinutes, setDayMaxMinutes] = useState({});
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewStale, setPreviewStale] = useState(false);
  const [editMenuOpen, setEditMenuOpen] = useState(false);
  const [missingInformation, setMissingInformation] = useState([]);
  const [creatorClosed, setCreatorClosed] = useState(false);

  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;
  const busyRef = useRef(false);
  const previewRequestRef = useRef({ fingerprint: "", id: "" });
  const lifecycleIdsRef = useRef(null);
  const formStateRef = useRef({});

  const categories = useMemo(() => categoriesForSport(sportId), [sportId]);
  const goalOptions = useMemo(() => categoriesForSportWithCustom(sportId), [sportId]);
  const isCustomGoal = goalCategory === CUSTOM_GOAL_CATEGORY;
  const showEventDate = !isCustomGoal && isEventCategory(goalCategory);
  const showFtp = !isCustomGoal && sportId === "cycling" && goalCategory === "improve_ftp";
  const showDistance = !isCustomGoal && [
    "ride_longer_without_fatigue",
    "prepare_for_audax",
    "complete_target_distance",
    "increase_distance_safely",
    "improve_endurance",
  ].includes(goalCategory);
  const finishFirstBlocked = hasLivePlan && activePlanConflictChoice === "FINISH_CURRENT_PLAN_FIRST";
  const previewRef = useRef(null);
  previewRef.current = preview;

  formStateRef.current = {
    sportId,
    goalCategory,
    customGoalName,
    originalText,
    targetDate,
    horizonMode,
    horizonMonths,
    noEventDate,
    distanceKm,
    ftpWatts,
    weekdays,
    dayMaxMinutes,
    preferredLongSessionDay,
    longestSessionMinutes,
    startPreference,
    customStartDate,
    planHorizonWeeks,
    activePlanConflictChoice,
    hasLivePlan,
  };

  const markPreviewStale = () => {
    if (!previewRef.current) return;
    previewRef.current = null;
    lifecycleIdsRef.current = null;
    setPreview(null);
    setPreviewStale(true);
  };

  const exitSetup = () => {
    setCreatorClosed(false);
    setStep(1);
    setPreview(null);
    setPreviewStale(false);
    setError(null);
    setStatus(null);
    setEditMenuOpen(false);
    lifecycleIdsRef.current = null;
  };

  useEffect(() => {
    if (!focusToken) return;
    setCreatorClosed(false);
    setStep(1);
    setEditMenuOpen(false);
    document.getElementById("training-goal")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [focusToken]);

  useEffect(() => {
    if (!incomingDraft || incomingDraft.status !== "VALIDATED_DRAFT" || !incomingDraft.draft_ref) {
      return;
    }
    setPreview(incomingDraft);
    setPreviewStale(false);
    setStep(6);
    setEditMenuOpen(false);
    lifecycleIdsRef.current = {
      confirm: newRequestId("confirm"),
      materialise: newRequestId("materialise"),
      activate: newRequestId("activate"),
    };
    if (typeof onIncomingDraftConsumed === "function") onIncomingDraftConsumed();
  }, [incomingDraft, onIncomingDraftConsumed]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [goals, profile, availability, planPrefill, activePlan, lifecycle] = await Promise.all([
          loadGoals(),
          loadProfile(),
          loadAvailability(),
          loadQuickPlanPrefill({ loadGoals, loadIntake, loadActivePlan }),
          loadActivePlan().catch(() => null),
          typeof loadCurrentLifecycle === "function"
            ? loadCurrentLifecycle().catch(() => null)
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        const primary = (goals?.items || []).find((item) => item.is_primary)
          || (goals?.items || [])[0]
          || null;
        setCurrentGoal(primary);
        if (typeof onSavedRef.current === "function" && primary) {
          onSavedRef.current({ goal: primary, profile });
        }
        if (primary) {
          const sports = primary.sports || [];
          const inferredSport = primary.sport
            || (sports.includes("swimming") && sports.includes("running")
              ? "triathlon"
              : sports.includes("running") && sports.includes("cycling")
                ? "duathlon"
                : sports[0] || "cycling");
          setSportId(inferredSport);
          const known = categoriesForSport(inferredSport);
          const generalIsPredefined = known.some((item) => item.value === CUSTOM_GOAL_STORAGE_CATEGORY);
          const storedCustom = primary.goal_category === CUSTOM_GOAL_STORAGE_CATEGORY
            && (!generalIsPredefined || Boolean(primary.goal_subtype));
          if (storedCustom) {
            setGoalCategory(CUSTOM_GOAL_CATEGORY);
            setCustomGoalName(primary.goal_subtype || "");
          } else if (known.some((item) => item.value === primary.goal_category)) {
            setGoalCategory(primary.goal_category);
            setCustomGoalName("");
          } else {
            setGoalCategory(known[0]?.value || CUSTOM_GOAL_CATEGORY);
            setCustomGoalName("");
          }
          setOriginalText(primary.original_text || "");
          if (primary.target_date) {
            setTargetDate(primary.target_date);
            setHorizonMode("custom");
          } else {
            setTargetDate("");
            setHorizonMode("unset");
          }
          if (primary.target?.metric_type === "ftp") {
            setFtpWatts(String(primary.target.value || ""));
          }
          if (
            ["target_distance", "running_distance", "swimming_distance"]
              .includes(primary.target?.metric_type)
          ) {
            setDistanceKm(String(primary.target.value || ""));
          }
        }
        if (profile?.primary_sport && !primary?.sport) {
          setSportId(profile.primary_sport);
        }
        setWeekdays(selectedWeekdaysFromAvailability(availability));
        setExistingAvailability(availability || null);
        setDayMaxMinutes(dayMaxMinutesFromAvailability(availability));
        const longest = availability?.general_constraints?.longest_session_minutes
          || availability?.longest_session_minutes;
        if (longest) setLongestSessionMinutes(String(longest));
        setStartPreference(planPrefill.startPreference || "START_TOMORROW");
        setCustomStartDate(planPrefill.customStartDate || "");
        setPreferredLongSessionDay(
          planPrefill.preferredLongSessionDay
          || availability?.general_constraints?.preferred_long_session_day
          || "",
        );
        const livePlan = Boolean(activePlan?.block_id);
        setHasLivePlan(livePlan);
        setActivePlanConflictChoice(
          livePlan ? (planPrefill.activePlanConflictChoice || "") : "",
        );
        if (Number.isFinite(planPrefill.planHorizonWeeks)) {
          setPlanHorizonWeeks(planPrefill.planHorizonWeeks);
        }
        const resumed = lifecycle?.draft?.status === "VALIDATED_DRAFT"
          && lifecycle.draft.draft_ref
          && lifecycle.activation?.status !== "ACTIVE";
        if (resumed) {
          setPreview(lifecycle.draft);
          setPreviewStale(false);
          setStep(6);
          lifecycleIdsRef.current = {
            confirm: newRequestId("confirm"),
            materialise: newRequestId("materialise"),
            activate: newRequestId("activate"),
          };
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.goalError?.message || err?.message || "Could not load your training goal.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [
    loadActivePlan,
    loadAvailability,
    loadCurrentLifecycle,
    loadGoals,
    loadIntake,
    loadProfile,
  ]);

  useEffect(() => {
    const syncLivePlan = async () => {
      try {
        const activePlan = await loadActivePlan();
        setHasLivePlan(Boolean(activePlan?.block_id));
        setPlanHorizonWeeks((current) => current || resolveQuickPlanHorizonWeeks(activePlan));
      } catch (_err) {
        // Keep the current selector when the active plan cannot be read.
      }
    };
    window.addEventListener(ACTIVE_PLAN_CHANGED_EVENT, syncLivePlan);
    return () => window.removeEventListener(ACTIVE_PLAN_CHANGED_EVENT, syncLivePlan);
  }, [loadActivePlan]);

  const toggleWeekday = (value) => {
    markPreviewStale();
    setWeekdays((current) => {
      if (current.includes(value)) {
        return current.filter((day) => day !== value);
      }
      setDayMaxMinutes((caps) => ({
        ...caps,
        [value]: caps[value]
          || resolveDaySessionCap({
            weekday: value,
            dayMaxMinutes: caps,
            longestSessionMinutes,
          }),
      }));
      return [...current, value].sort((a, b) => a - b);
    });
  };

  const updateDayMinutes = (weekday, rawValue) => {
    markPreviewStale();
    const parsed = Number.parseInt(rawValue, 10);
    setDayMaxMinutes((caps) => ({
      ...caps,
      [weekday]: Number.isFinite(parsed) ? parsed : "",
    }));
  };

  const changeSport = (nextSport) => {
    markPreviewStale();
    setSportId(nextSport);
    const nextCategories = categoriesForSport(nextSport);
    if (
      goalCategory !== CUSTOM_GOAL_CATEGORY
      && !nextCategories.some((item) => item.value === goalCategory)
    ) {
      setGoalCategory(nextCategories[0]?.value || CUSTOM_GOAL_CATEGORY);
    }
  };

  const generatePreview = async () => {
    const state = formStateRef.current;
    const invalid = creatorValidationError(state, 5);
    if (invalid) {
      setError(invalid.message);
      setStep(invalid.slide);
      return;
    }
    if (busyRef.current) return;
    busyRef.current = true;
    setSaving(true);
    setError(null);
    setStatus(null);
    setMissingInformation([]);
    try {
      const decision = typeof loadCoachingDecision === "function"
        ? await loadCoachingDecision()
        : null;
      if (coachingDecisionBlocksPlanGeneration(decision, {
        allowProposedContextPreview: true,
      })) {
        throw new Error(coachingDecisionPrerequisiteMessage(decision));
      }

      let resolvedDate = state.targetDate;
      if (!showEventDate && state.horizonMode === "months") {
        resolvedDate = targetDateFromHorizonMonths(Number(state.horizonMonths));
      } else if (!showEventDate && state.horizonMode === "unset") {
        resolvedDate = "";
      }
      const goalPayload = buildAthleteGoalPayload({
        sportId: state.sportId,
        goalCategory: state.goalCategory === CUSTOM_GOAL_CATEGORY
          ? CUSTOM_GOAL_STORAGE_CATEGORY
          : state.goalCategory,
        originalText: state.originalText,
        targetDate: resolvedDate,
        noEventDate: state.noEventDate,
        distanceKm: showDistance ? state.distanceKm : "",
        ftpWatts: showFtp ? state.ftpWatts : "",
        goalSubtype: state.goalCategory === CUSTOM_GOAL_CATEGORY
          ? state.customGoalName
          : (currentGoal?.goal_subtype ? null : undefined),
        existingGoal: currentGoal,
      });
      const proposedTrainingContext = buildProposedTrainingContext({
        sportId: state.sportId,
        goalPayload,
        weekdays: state.weekdays,
        dayMaxMinutes: state.dayMaxMinutes,
        preferredLongSessionDay: state.preferredLongSessionDay,
        longestSessionMinutes: state.longestSessionMinutes,
        startPreference: state.startPreference,
        customStartDate: state.customStartDate,
        activePlanConflictChoice: state.activePlanConflictChoice,
        hasLivePlan: state.hasLivePlan,
        horizonWeeks: state.planHorizonWeeks,
      });

      const previewResult = await previewStart({
        startPreference: state.startPreference,
        customStartDate: state.startPreference === "CUSTOM_DATE" ? state.customStartDate : null,
        activePlanConflictChoice: state.hasLivePlan ? state.activePlanConflictChoice : null,
      });
      if (previewResult?.active_plan_conflict && !state.activePlanConflictChoice) {
        const err = new Error(
          "Your current plan overlaps this start date. Choose whether to replace it or finish it first.",
        );
        err.planConflict = true;
        throw err;
      }

      const fingerprint = previewFingerprint(state);
      if (previewRequestRef.current.fingerprint !== fingerprint) {
        previewRequestRef.current = { fingerprint, id: newRequestId("preview") };
      }
      const raw = await generatePlan({
        horizonWeeks: Number(state.planHorizonWeeks),
        clientRequestId: previewRequestRef.current.id,
        proposedTrainingContext,
      });
      if (raw?.status === "IDEMPOTENCY_CONFLICT") {
        previewRequestRef.current = { fingerprint: "", id: "" };
        throw new Error(raw.reply || "That preview no longer matches. Try again.");
      }
      const outcome = interpretPlanGenerationResult(raw);
      if (!outcome.ok || outcome.kind !== PLAN_GENERATION_RESULT_KIND.DRAFT_READY || !raw?.draft_ref) {
        const codes = raw?.missing_information || [];
        setMissingInformation(codes);
        throw new Error(outcome.message || "Could not generate your training plan.");
      }
      if (raw.material_action_taken === true) {
        throw new Error("Preview generation tried to change the active plan. It was stopped.");
      }
      lifecycleIdsRef.current = {
        confirm: newRequestId("confirm"),
        materialise: newRequestId("materialise"),
        activate: newRequestId("activate"),
      };
      setPreview(raw);
      setPreviewStale(false);
      setEditMenuOpen(false);
      setStep(6);
      setStatus("Training plan draft ready. Review it before you start.");
    } catch (err) {
      if (err?.planError?.code === "idempotency_conflict") {
        previewRequestRef.current = { fingerprint: "", id: "" };
      }
      setPreview(null);
      setPreviewStale(false);
      lifecycleIdsRef.current = null;
      setError(err?.planConflict
        ? "Your current plan overlaps this start date. Choose whether to replace it or finish it first."
        : (err?.message || "Could not generate a preview."));
    } finally {
      busyRef.current = false;
      setSaving(false);
    }
  };

  const handleStart = async () => {
    if (busyRef.current || previewStale || !preview?.draft_ref) return;
    if (finishFirstBlocked) return;
    if (!lifecycleIdsRef.current) return;
    busyRef.current = true;
    setSaving(true);
    setError(null);
    try {
      const result = await startPlan({
        draftResponse: preview,
        activePlanConflictChoice: hasLivePlan ? activePlanConflictChoice : null,
        requestIds: lifecycleIdsRef.current,
      });
      const startDate = preview?.draft?.block_start_date || preview?.block_start_date;
      const startSentence = startDate
        ? `Workouts begin on ${startDate}, including when that date is still ahead.`
        : "Workouts begin on the plan start date, including when that date is still ahead.";
      setStatus(result?.replaced
        ? `Your new training plan replaced the current one. Completed workouts stay in your history. ${startSentence}`
        : `Your training plan is now the current plan. ${startSentence}`);
      setCreatorClosed(true);
      setPreview(null);
      document.getElementById("active-training-plan")?.scrollIntoView?.({
        behavior: "smooth",
        block: "start",
      });
      if (typeof onPlanGenerated === "function") onPlanGenerated(result);
    } catch (err) {
      const code = err?.cause?.planError?.code || err?.planError?.code;
      if (
        code === "proposed_settings_conflict"
        || code === "draft_context_stale"
        || code === "expected_active_plan_mismatch"
      ) {
        setPreviewStale(true);
      }
      if (err?.code === FUTURE_PLAN_NOT_SUPPORTED) {
        setError(futurePlanBlockerMessage());
      } else {
        setError(quickPlanLifecycleErrorMessage(err));
      }
    } finally {
      busyRef.current = false;
      setSaving(false);
    }
  };

  const goNext = async () => {
    if (step < 5) {
      const invalid = creatorValidationError(formStateRef.current, step);
      if (invalid) {
        setError(invalid.message);
        return;
      }
      setError(null);
      setStep(step + 1);
      return;
    }
    if (step === 5 && preview && !previewStale) {
      setStep(6);
      return;
    }
    await generatePreview();
  };

  const goBack = () => {
    setError(null);
    setStep((current) => Math.max(1, current - 1));
  };

  const jumpToSlide = (target) => {
    if (target < 1 || target > 5) return;
    if (target > step) {
      const invalid = creatorValidationError(formStateRef.current, target - 1);
      if (invalid) {
        setError(invalid.message);
        setStep(invalid.slide);
        return;
      }
    }
    setError(null);
    setEditMenuOpen(false);
    setStep(target);
  };

  const goalLabel = isCustomGoal
    ? (customGoalName || "Custom goal")
    : (categories.find((item) => item.value === goalCategory)?.label || goalCategory);
  const startLabel = START_PREFERENCE_OPTIONS.find((item) => item.id === startPreference)?.label
    || startPreference;
  const selectedDayLabels = weekdays.map((day) => (
    WEEKDAY_OPTIONS.find((item) => item.value === day)?.label || WEEKDAY_NAMES[day] || day
  ));
  const weeklyAvailableMinutes = weekdays.reduce((total, day) => {
    const minutes = Number(dayMaxMinutes[day]);
    return total + (Number.isFinite(minutes) ? minutes : 0);
  }, 0);

  const slideBody = (
    <div className="space-y-3" data-testid={`training-plan-slide-${step}`}>
      {step === 1 && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="training-plan-sport-goal">
          <label className="block min-w-0">
            <span className="card-label">Main sport</span>
            <select
              className={`${inputCls} mt-2`}
              value={sportId}
              onChange={(event) => changeSport(event.target.value)}
              data-testid="training-goal-sport"
            >
              {TRAINING_GOAL_SPORTS.map((sport) => (
                <option key={sport.id} value={sport.id}>{sport.label}</option>
              ))}
            </select>
          </label>
          <label className="block min-w-0">
            <span className="card-label">Primary goal</span>
            <select
              className={`${inputCls} mt-2`}
              value={goalCategory}
              onChange={(event) => {
                markPreviewStale();
                setGoalCategory(event.target.value);
              }}
              data-testid="training-goal-category"
            >
              {goalOptions.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
          </label>
          </div>
          {isCustomGoal && (
            <>
              <label className="block">
                <span className="card-label">Custom goal name</span>
                <input
                  className={`${inputCls} mt-2`}
                  value={customGoalName}
                  maxLength={120}
                  onChange={(event) => {
                    markPreviewStale();
                    setCustomGoalName(event.target.value);
                  }}
                  data-testid="training-goal-custom-name"
                />
              </label>
              <p className="text-sm text-dark-text-secondary" data-testid="training-goal-custom-note">
                A custom goal is saved with your name and description. The plan uses the existing general-fitness training for this sport. The custom text does not create a new training calculation.
              </p>
            </>
          )}
          <label className="block">
            <span className="card-label">Describe it</span>
            <textarea
              className={`${inputCls} mt-2`}
              rows={3}
              value={originalText}
              onChange={(event) => {
                markPreviewStale();
                setOriginalText(event.target.value);
              }}
              placeholder="Example: Ride 100 km comfortably without fading"
              data-testid="training-goal-text"
              required
            />
          </label>
        </>
      )}

      {step === 2 && (
        <>
          {!showEventDate && horizonMode === "unset" && (
            <p className="text-sm text-dark-text-secondary" data-testid="training-goal-set-date-prompt">
              Set a target date if you want Endurava to plan several training blocks toward a deadline. This is not filled in from phrases like “in 4 months”.
            </p>
          )}
          {!showEventDate && horizonMode === "custom" && !targetDate && (
            <p className="text-sm text-dark-text-secondary" data-testid="training-goal-set-date-prompt">
              Choose the calendar date for your goal deadline. This is not the length of the next training block.
            </p>
          )}
          {!showEventDate && (horizonMode === "months" || (horizonMode === "custom" && targetDate)) && (
            <p className="text-sm text-dark-text-secondary" data-testid="training-goal-set-date-prompt">
              This date is your goal deadline. The length of the next training block is chosen on the Schedule step.
            </p>
          )}
          {showEventDate && (
            <div className="space-y-2">
              <label className="block">
                <span className="card-label">Target event date (optional)</span>
                <input
                  type="date"
                  className={`${inputCls} mt-2`}
                  value={targetDate}
                  onChange={(event) => {
                    markPreviewStale();
                    setTargetDate(event.target.value);
                  }}
                  disabled={noEventDate}
                  data-testid="training-goal-date"
                />
              </label>
              <label className="flex items-center gap-2 text-sm text-dark-text-secondary">
                <input
                  type="checkbox"
                  checked={noEventDate}
                  onChange={(event) => {
                    markPreviewStale();
                    setNoEventDate(event.target.checked);
                  }}
                  data-testid="training-goal-no-date"
                />
                I don&apos;t have an event date
              </label>
            </div>
          )}
          {!showEventDate && (
            <div className="space-y-2">
              <label className="block">
                <span className="card-label">Goal deadline preference</span>
                <select
                  className={`${inputCls} mt-2`}
                  value={horizonMode === "months" ? `months-${horizonMonths}` : horizonMode}
                  onChange={(event) => {
                    markPreviewStale();
                    const value = event.target.value;
                    if (value === "unset" || value === "custom") {
                      setHorizonMode(value);
                      return;
                    }
                    if (value.startsWith("months-")) {
                      setHorizonMode("months");
                      setHorizonMonths(value.replace("months-", ""));
                    }
                  }}
                  data-testid="training-goal-horizon"
                >
                  <option value="unset">Not set yet</option>
                  {GOAL_HORIZON_MONTH_OPTIONS.map((months) => (
                    <option key={months} value={`months-${months}`}>
                      {months} months
                    </option>
                  ))}
                  <option value="custom">Custom date</option>
                </select>
              </label>
              {horizonMode === "custom" && (
                <label className="block">
                  <span className="card-label">Target date</span>
                  <input
                    type="date"
                    className={`${inputCls} mt-2`}
                    value={targetDate}
                    onChange={(event) => {
                      markPreviewStale();
                      setTargetDate(event.target.value);
                    }}
                    data-testid="training-goal-custom-date"
                  />
                </label>
              )}
            </div>
          )}
          <p className="text-xs text-dark-text-muted" data-testid="training-goal-deadline-note">
            This is the goal deadline, not the length of the next training block.
          </p>
          {showDistance && (
            <label className="block">
              <span className="card-label">
                {sportId === "swimming" ? "Target distance (metres, optional)" : "Target distance (km, optional)"}
              </span>
              <input
                type="number"
                min="0"
                step="0.1"
                className={`${inputCls} mt-2`}
                value={distanceKm}
                onChange={(event) => {
                  markPreviewStale();
                  setDistanceKm(event.target.value);
                }}
                data-testid="training-goal-distance"
              />
            </label>
          )}
          {showFtp && (
            <label className="block">
              <span className="card-label">Target FTP (watts, optional)</span>
              <input
                type="number"
                min="0"
                className={`${inputCls} mt-2`}
                value={ftpWatts}
                onChange={(event) => {
                  markPreviewStale();
                  setFtpWatts(event.target.value);
                }}
                data-testid="training-goal-ftp"
              />
            </label>
          )}
        </>
      )}

      {step === 3 && (
        <fieldset aria-labelledby="training-plan-step-heading">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            {WEEKDAY_OPTIONS.map((day) => {
              const selected = weekdays.includes(day.value);
              return (
                <label
                  key={day.value}
                  className={`flex min-h-11 cursor-pointer items-center justify-center rounded-md border px-2 py-2 text-center text-sm ${
                    selected
                      ? "border-brand-primary bg-brand-primary/10 text-brand-primary"
                      : "border-[var(--border-primary)] text-dark-text-secondary"
                  }`}
                >
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={selected}
                    onChange={() => toggleWeekday(day.value)}
                    data-testid={`training-goal-day-${day.value}`}
                  />
                  {WEEKDAY_NAMES[day.value]}
                </label>
              );
            })}
          </div>
          <p className="mt-3 text-sm text-dark-text-secondary" data-testid="training-plan-day-summary">
            {weekdays.length} training day{weekdays.length === 1 ? "" : "s"}
            {selectedDayLabels.length > 0 ? `: ${selectedDayLabels.join(", ")}` : ""}
            {` · ${7 - weekdays.length} unavailable day${7 - weekdays.length === 1 ? "" : "s"}`}
          </p>
        </fieldset>
      )}

      {step === 4 && (
        <>
          {weekdays.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {weekdays.map((weekday) => {
                const label = WEEKDAY_NAMES[weekday]
                  || WEEKDAY_OPTIONS.find((day) => day.value === weekday)?.label
                  || weekday;
                return (
                  <label key={weekday} className="block">
                    <span className="card-label">{label} minutes</span>
                    <input
                      type="number"
                      min="5"
                      max="600"
                      className={`${inputCls} mt-2`}
                      value={dayMaxMinutes[weekday] ?? ""}
                      onChange={(event) => updateDayMinutes(weekday, event.target.value)}
                      data-testid={`training-goal-day-minutes-${weekday}`}
                      required
                    />
                  </label>
                );
              })}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block min-w-0">
            <span className="card-label">Preferred long session day (optional)</span>
            <select
              className={`${inputCls} mt-2`}
              value={preferredLongSessionDay}
              onChange={(event) => {
                markPreviewStale();
                setPreferredLongSessionDay(event.target.value);
              }}
              data-testid="training-goal-long-day"
            >
              <option value="">No preference</option>
              {weekdays.map((weekday) => (
                <option key={weekday} value={ISO_WEEKDAY_NAMES[weekday]}>
                  {WEEKDAY_NAMES[weekday] || WEEKDAY_OPTIONS.find((day) => day.value === weekday)?.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block min-w-0">
            <span className="card-label">Typical longest session (minutes)</span>
            <input
              type="number"
              min="15"
              max="720"
              className={`${inputCls} mt-2`}
              value={longestSessionMinutes}
              onChange={(event) => {
                markPreviewStale();
                setLongestSessionMinutes(event.target.value);
              }}
              data-testid="training-goal-longest-session"
            />
          </label>
          </div>
          <p className="text-sm text-dark-text-secondary" data-testid="training-plan-weekly-minutes">
            Weekly available time: {weeklyAvailableMinutes} min
          </p>
        </>
      )}

      {step === 5 && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block min-w-0">
            <span className="card-label">Start preference</span>
            <select
              className={`${inputCls} mt-2`}
              value={startPreference}
              onChange={(event) => {
                markPreviewStale();
                setStartPreference(event.target.value);
              }}
              data-testid="quick-plan-start-preference"
            >
              {START_PREFERENCE_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>{option.label}</option>
              ))}
            </select>
            <span className="mt-1 block text-xs text-dark-text-muted" data-testid="training-plan-start-note">
              Workouts begin on this date. Save & Start makes the plan current immediately, including when the date is still ahead.
            </span>
          </label>
          <label className="block min-w-0">
            <span className="card-label">Training block length</span>
            <select
              className={`${inputCls} mt-2`}
              value={planHorizonWeeks}
              onChange={(event) => {
                markPreviewStale();
                setPlanHorizonWeeks(Number(event.target.value));
              }}
              data-testid="quick-plan-duration-weeks"
            >
              {QUICK_PLAN_DURATION_OPTIONS.map((weeks) => (
                <option key={weeks} value={weeks}>
                  {weeks} week{weeks === 1 ? "" : "s"}
                </option>
              ))}
            </select>
            <span className="mt-1 block text-xs text-dark-text-muted">
              Length of the next training block, up to 10 weeks. The goal deadline stays on the Target step.
            </span>
          </label>
          </div>
          {startPreference === "CUSTOM_DATE" && (
            <label className="block">
              <span className="card-label">Custom start date</span>
              <input
                type="date"
                className={`${inputCls} mt-2`}
                value={customStartDate}
                onChange={(event) => {
                  markPreviewStale();
                  setCustomStartDate(event.target.value);
                }}
                data-testid="quick-plan-custom-start-date"
              />
            </label>
          )}
          {hasLivePlan && (
            <fieldset className="space-y-2" data-testid="quick-plan-current-plan-choices">
              <legend className="card-label">If your current plan overlaps this start</legend>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="active-plan-conflict"
                  checked={activePlanConflictChoice === "REPLACE_CURRENT_PLAN"}
                  onChange={() => {
                    markPreviewStale();
                    setActivePlanConflictChoice("REPLACE_CURRENT_PLAN");
                  }}
                  data-testid="quick-plan-replace-current"
                />
                Replace my current plan
              </label>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="active-plan-conflict"
                  checked={activePlanConflictChoice === "FINISH_CURRENT_PLAN_FIRST"}
                  onChange={() => {
                    markPreviewStale();
                    setActivePlanConflictChoice("FINISH_CURRENT_PLAN_FIRST");
                  }}
                  data-testid="quick-plan-finish-current"
                />
                Finish my current plan first
              </label>
              <p className="text-xs text-dark-text-muted">
                Finishing the current plan first only dates the preview after that plan. It cannot start a second plan automatically.
              </p>
            </fieldset>
          )}
        </>
      )}

      {step === 6 && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm" data-testid="training-plan-summary">
            <p><span className="card-label">Sport</span><br />{sportConfig(sportId).label || sportId}</p>
            <p><span className="card-label">Goal</span><br />{goalLabel}</p>
            <p className="sm:col-span-2"><span className="card-label">Description</span><br />{originalText}</p>
            <p><span className="card-label">Deadline</span><br />{targetDate || "Not set"}</p>
            <p><span className="card-label">Training block</span><br />{planHorizonWeeks} weeks</p>
            <p><span className="card-label">Starts</span><br />{startLabel}{customStartDate ? ` (${customStartDate})` : ""}</p>
            <p>
              <span className="card-label">Days</span><br />
              {weekdays.map((day) => {
                const label = WEEKDAY_OPTIONS.find((item) => item.value === day)?.label || day;
                return `${label} ${dayMaxMinutes[day] || ""}m`;
              }).join(", ") || "None"}
            </p>
          </div>
          {previewStale && (
            <p className="text-sm text-analytics-amber" data-testid="training-plan-preview-stale">
              This preview is out of date. Update it before starting.
            </p>
          )}
          {preview?.draft && !previewStale && (
            <TrainingPlanDraftReview
              draft={preview.draft}
              draftRef={preview.draft_ref}
              weekSelector
              compact
            />
          )}
          {missingInformation.length > 0 && (
            <ul className="text-sm text-dark-text-secondary">
              {missingInformation.map((code) => (
                <li key={code}>{describeMissingInformation(code).message}</li>
              ))}
            </ul>
          )}
          {editMenuOpen && (
            <div className="flex flex-wrap gap-2" role="group" aria-label="Edit a section" data-testid="training-plan-edit-menu">
              {SLIDES.slice(0, 5).map((slide, index) => (
                <Button
                  key={slide.id}
                  type="button"
                  variant="outline"
                  onClick={() => jumpToSlide(index + 1)}
                  data-testid={`training-plan-edit-${index + 1}`}
                >
                  {slide.title}
                </Button>
              ))}
            </div>
          )}
          {finishFirstBlocked && !previewStale && (
            <p className="text-sm text-dark-text-secondary" data-testid="training-plan-schedule-blocker">
              {futurePlanBlockerMessage()}
            </p>
          )}
        </div>
      )}
    </div>
  );

  return (
    <CoachingSectionCard
      title="Create or change training plan"
      subtitle="Six short steps. Next builds a preview from these answers and does not change your saved goal or schedule. Save & Start applies them with the plan."
      headingId="training-goal-heading"
      testId="training-goal-setup"
    >
      <div id="training-goal" className="scroll-mt-24" />
      {loading ? (
        <p className="text-sm text-dark-text-secondary">Loading your plan controls…</p>
      ) : creatorClosed ? (
        <p data-testid="training-plan-started" className="text-sm text-dark-text-secondary">
          The plan creator is closed. Your new plan is in Active Training Plan.
        </p>
      ) : (
        <div className="space-y-3" data-testid="training-goal-form">
          <nav aria-label="Plan steps" data-testid="training-plan-stepper">
            <ol className="grid grid-cols-6 gap-1 sm:gap-2">
              {SLIDES.map((slide, index) => {
                const number = index + 1;
                const current = step === number;
                const locked = number === 6 && (!preview || previewStale);
                return (
                  <li key={slide.id}>
                    <button
                      type="button"
                      className={`w-full rounded-md border px-1 py-2 text-center text-[10px] sm:text-xs ${
                        current
                          ? "border-brand-primary text-brand-primary"
                          : "border-[var(--border-primary)] text-dark-text-secondary"
                      }`}
                      aria-current={current ? "step" : undefined}
                      disabled={locked || saving}
                      onClick={() => {
                        if (number === 6 && preview && !previewStale) {
                          setStep(6);
                          return;
                        }
                        if (number < 6) jumpToSlide(number);
                      }}
                      data-testid={`training-plan-step-${number}`}
                    >
                      <span className="block font-semibold">{number}</span>
                      <span className="hidden sm:block">{slide.short}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </nav>
          <div>
            <h3 id="training-plan-step-heading" className="section-title text-base">{SLIDES[step - 1].title}</h3>
          </div>
          {slideBody}
          <div className="flex flex-wrap items-center gap-2">
            {step > 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={goBack}
                disabled={saving}
                data-testid="training-plan-back"
              >
                Back
              </Button>
            )}
            {step < 6 && !previewStale && (
              <Button
                type="button"
                onClick={goNext}
                disabled={saving}
                data-testid="training-plan-next"
                className="btn-primary-app"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                    Building preview…
                  </>
                ) : (
                  "Next"
                )}
              </Button>
            )}
            {previewStale && (
              <Button
                type="button"
                onClick={generatePreview}
                disabled={saving}
                data-testid="training-plan-update-preview"
                className="btn-primary-app"
              >
                {saving ? "Updating preview…" : "Update Preview"}
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={exitSetup}
              disabled={saving}
              data-testid="training-plan-exit"
            >
              Exit Setup
            </Button>
            {step === 6 && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditMenuOpen((open) => !open)}
                disabled={saving}
                data-testid="training-plan-edit"
              >
                Edit Plan
              </Button>
            )}
            {step === 6 && !finishFirstBlocked && (
              <Button
                type="button"
                onClick={handleStart}
                disabled={saving || previewStale || !preview?.draft_ref}
                data-testid="training-plan-save-start"
                className="btn-primary-app w-full basis-full sm:w-auto sm:basis-auto"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                    Starting…
                  </>
                ) : (
                  "Save & Start"
                )}
              </Button>
            )}
          </div>
        </div>
      )}
      {error && (
        <p role="alert" data-testid="training-goal-error" className="mt-4 text-sm text-analytics-amber">
          {error}
        </p>
      )}
      {status && (
        <p data-testid="training-goal-status" className="mt-4 text-sm text-dark-text-secondary">
          {status}
        </p>
      )}
    </CoachingSectionCard>
  );
};

export default TrainingGoalSetup;
